/**
 * loadPlaces — one memoised promise for the Google Places script.
 *
 * WHY THIS EXISTS (plan_client_form_modal_2026_10_02 §4.7). The code this
 * replaces decided in an effect:
 *
 *   if (!document.getElementById("google-places-script")) { inject; onload = setRefs }
 *   else if (window.google?.maps?.places)                 { setRefs }
 *
 * There is a third state those two branches do not cover: **the tag is present
 * but the API has not finished loading.** Neither branch runs, the service refs
 * stay null for the life of that mount, and `getPlacePredictions` is never
 * reachable — address autocomplete is silently dead, with no error anywhere.
 *
 * That was survivable while the only hosts mounted once per page visit. It is
 * not survivable in a MODAL: `BdrModal` unmounts its children on close, so
 * opening the form, closing it before the script lands, and reopening puts you
 * squarely in the uncovered state — and the first load's `onload` writes its
 * services into the refs of an instance React has already thrown away.
 *
 * So loading becomes a promise, resolved once per page load and shared:
 *   - API already present            -> resolve immediately
 *   - tag present, still loading     -> attach to THAT tag's load/error
 *   - no tag                         -> inject, then attach
 *
 * Callers await it behind an `alive` guard so a resolve after unmount writes
 * nothing. The script id stays `google-places-script` — GetAQuote shares it,
 * and a second tag would load the API twice.
 */

const SCRIPT_ID = "google-places-script";
const SRC =
  "https://maps.googleapis.com/maps/api/js?key=AIzaSyAajOAIHFb3kziY3a1UVz5Ler1y8nGxCr4&libraries=places";

/** The single in-flight/settled promise for this page load. */
let placesPromise = null;

const apiReady = () =>
  typeof window !== "undefined" && !!window.google?.maps?.places;

const loadPlaces = () => {
  if (apiReady()) return Promise.resolve(window.google.maps.places);
  if (placesPromise) return placesPromise;

  placesPromise = new Promise((resolve, reject) => {
    const settle = () => {
      if (apiReady()) {
        resolve(window.google.maps.places);
        return;
      }
      /* The tag fired `load` but the namespace is absent — a blocked or
         failed script. Clear the memo so a later caller can retry rather than
         inheriting a permanently rejected promise. */
      placesPromise = null;
      reject(new Error("Google Places loaded without maps.places"));
    };

    const fail = () => {
      placesPromise = null;
      reject(new Error("Google Places script failed to load"));
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      /* THE CASE THE OLD CODE MISSED: present but not yet loaded. */
      existing.addEventListener("load", settle, { once: true });
      existing.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SRC;
    script.async = true;
    script.addEventListener("load", settle, { once: true });
    script.addEventListener("error", fail, { once: true });
    document.head.appendChild(script);
  });

  return placesPromise;
};

export default loadPlaces;
